import { z } from "zod";

import {
  breakerKindSchema,
  breakerSelectivityStatusSchema,
  breakerSelectivityWarningCodeSchema,
  deviceRoleSchema,
  dutyBasisSchema,
  faultTypeSchema,
  prospectiveCurrentSourceSchema,
  selectivityKindSchema,
  shortCircuitCaseSchema,
} from "./breakerSelectivityContract";
import { calculationRunSummarySchema } from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import { exactDecimalSchema } from "./faultContract";
import { CHOOSE_A_FAULT_RUN, formatApiError, requestSignal } from "./faultLink";
import { ApiError } from "./http";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

const RUNS_URL = "/api/v1/electrical/breaker-selectivity/runs";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The choices a device needs; the backend imposes no default (A21 (b)). */
export const REQUIRED_DEVICE_CHOICES = ["kind", "duty_basis"] as const;

// One circuit breaker as the manufacturer rates it (A21 (b)); nothing is defaulted.
// Ics is required for a SERVICE duty basis and may be declared, unchecked, for ULTIMATE.
const breakerDeviceShape = {
  code: z.string().trim().min(1).max(50),
  // The choices are optional here only so a missing one is reported by the rule below
  // as a missing value ("... is required."), the way the starter form reports its
  // choices; the backend requires every one of them.
  kind: breakerKindSchema.optional(),
  rated_current_a: exactDecimalSchema,
  rated_operational_voltage_v: exactDecimalSchema,
  ultimate_breaking_capacity_ka: exactDecimalSchema,
  making_capacity_ka: exactDecimalSchema,
  duty_basis: dutyBasisSchema.optional(),
  service_breaking_capacity_ka: exactDecimalSchema.optional(),
};

type BreakerDeviceShape = {
  kind?: z.infer<typeof breakerKindSchema>;
  duty_basis?: z.infer<typeof dutyBasisSchema>;
  service_breaking_capacity_ka?: string;
};

// An unmade choice is reported as a missing value, so the form says "<Label> is required."
function requireChoice(field: string, value: unknown, ctx: z.RefinementCtx): void {
  if (value === undefined) {
    ctx.addIssue({
      code: "invalid_type",
      expected: "string",
      received: "undefined",
      message: `${field} is required`,
      path: [field],
    });
  }
}

// Mirrors the backend rule (schemas/breaker_selectivity.py, validate_duty_basis).
function requireIcsForServiceBasis(device: BreakerDeviceShape, ctx: z.RefinementCtx): void {
  for (const field of REQUIRED_DEVICE_CHOICES) {
    requireChoice(field, device[field], ctx);
  }

  if (device.duty_basis === "SERVICE" && device.service_breaking_capacity_ka === undefined) {
    ctx.addIssue({
      code: "custom",
      message: "service_breaking_capacity_ka is required for a SERVICE duty basis",
      path: ["service_breaking_capacity_ka"],
    });
  }
}

export const breakerDeviceRequestSchema = z
  .object(breakerDeviceShape)
  .strict()
  .superRefine(requireIcsForServiceBasis);

// One outgoing device with its declared evidence towards the incomer (A21 (c), (d)).
// Whether selectivity is required is stated, never defaulted. The declared kind is
// TOTAL, PARTIAL with a limit, or left out when no evidence is held (REVIEW_REQUIRED).
// A cascading limit is optional. The form must omit a blank item instead of sending
// a value.
export const outgoingDeviceRequestSchema = z
  .object({
    ...breakerDeviceShape,
    // Stated, never defaulted (A21 (c)); optional here for the same "is required." rule.
    selectivity_required: z.boolean().optional(),
    declared_selectivity_kind: selectivityKindSchema.optional(),
    selectivity_limit_ka: exactDecimalSchema.optional(),
    cascading_limit_ka: exactDecimalSchema.optional(),
  })
  .strict()
  .superRefine((device, ctx) => {
    requireIcsForServiceBasis(device, ctx);
    requireChoice("selectivity_required", device.selectivity_required, ctx);

    // Mirrors the backend rule (validate_selectivity_declaration): a limit belongs to a
    // PARTIAL declaration, and a PARTIAL declaration needs its limit.
    const partial = device.declared_selectivity_kind === "PARTIAL";
    if (partial && device.selectivity_limit_ka === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "selectivity_limit_ka is required for a PARTIAL declaration",
        path: ["selectivity_limit_ka"],
      });
    }
    if (!partial && device.selectivity_limit_ka !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: "selectivity_limit_ka belongs to a PARTIAL declaration only",
        path: ["selectivity_limit_ka"],
      });
    }
  });

// A fault level typed by the engineer: allowed, but not linked evidence, so the study
// is REVIEW_REQUIRED (A21 (a)). Without the peak ip the making check is not made.
export const typedProspectiveCurrentSchema = z
  .object({
    prospective_current_ka: exactDecimalSchema,
    peak_current_ka: exactDecimalSchema.optional(),
    system_nominal_voltage_v: exactDecimalSchema,
    fault_bus_code: z.string().trim().min(1).max(50),
  })
  .strict();

export const breakerSelectivityRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    bus_code: z.string().trim().min(1).max(50),
    // Exactly one of the two: a stored EOS-04 fault run of the organization, or a typed
    // value. The rule below reports the pair the same way the backend does, and names a
    // blank or malformed run id with the selector's own sentence.
    fault_run_id: z.string().optional(),
    typed: typedProspectiveCurrentSchema.optional(),
    incomer: breakerDeviceRequestSchema,
    outgoing: z.array(outgoingDeviceRequestSchema).min(1).max(200),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    if ((values.fault_run_id === undefined) === (values.typed === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "give exactly one prospective current source: fault_run_id or typed",
        path: ["fault_run_id"],
      });
    } else if (values.fault_run_id !== undefined && !UUID.test(values.fault_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_FAULT_RUN, path: ["fault_run_id"] });
    }

    // Mirrors the backend rule: the incomer and every outgoing device share one code space.
    const codes = [values.incomer.code, ...values.outgoing.map((device) => device.code)];
    if (new Set(codes).size !== codes.length) {
      ctx.addIssue({
        code: "custom",
        message: "device codes must be unique within a study",
        path: ["outgoing"],
      });
    }
  });

export const breakerSelectivityWarningSchema = z
  .object({
    code: breakerSelectivityWarningCodeSchema,
    message: z.string(),
  })
  .strict();

// One device as checked: every input echoed, each check true / false / null (not
// made - no Ics held to, no peak, no declaration; null on the incomer for the
// selectivity items it has none of).
export const breakerDeviceResponseSchema = z
  .object({
    role: deviceRoleSchema,
    code: z.string(),
    kind: breakerKindSchema,
    rated_current_a: z.string(),
    rated_operational_voltage_v: z.string(),
    ultimate_breaking_capacity_ka: z.string(),
    making_capacity_ka: z.string(),
    duty_basis: dutyBasisSchema,
    service_breaking_capacity_ka: z.string().nullable(),
    cascading_limit_ka: z.string().nullable(),
    selectivity_required: z.boolean().nullable(),
    declared_selectivity_kind: selectivityKindSchema.nullable(),
    selectivity_limit_ka: z.string().nullable(),
    // Rating minus the fault level (negative when the check failed); null when not made.
    icu_margin_ka: z.string(),
    ics_margin_ka: z.string().nullable(),
    icm_margin_ka: z.string().nullable(),
    selectivity_margin_ka: z.string().nullable(),
    icu_adequate: z.boolean(),
    ics_adequate: z.boolean().nullable(),
    icm_adequate: z.boolean().nullable(),
    voltage_adequate: z.boolean(),
    relies_on_cascading: z.boolean().nullable(),
    selective: z.boolean().nullable(),
    status: breakerSelectivityStatusSchema,
    warnings: z.array(breakerSelectivityWarningSchema),
  })
  .strict();

export const breakerSelectivityResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    bus_code: z.string(),
    current_source: prospectiveCurrentSourceSchema,
    // The linked run, its case, fault type and bus exactly as the stored result holds
    // them; null run / case / type for a typed value. ip is null when it was not held.
    fault_run_id: z.string().uuid().nullable(),
    calculation_case: shortCircuitCaseSchema.nullable(),
    fault_type: faultTypeSchema.nullable(),
    fault_bus_code: z.string(),
    prospective_current_ka: z.string(),
    peak_current_ka: z.string().nullable(),
    system_nominal_voltage_v: z.string(),
    incomer: breakerDeviceResponseSchema,
    outgoing: z.array(breakerDeviceResponseSchema),
    status: breakerSelectivityStatusSchema,
    warnings: z.array(breakerSelectivityWarningSchema),
    jurisdiction_profile: jurisdictionProfileSchema,
    notes: z.string().nullable(),
  })
  .strict();

export const breakerSelectivityRunCreateRequestSchema = z
  .object({
    study: breakerSelectivityRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const breakerSelectivityRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: breakerSelectivityResponseSchema,
  })
  .strict();

export type BreakerDeviceRequest = z.infer<typeof breakerDeviceRequestSchema>;
export type OutgoingDeviceRequest = z.infer<typeof outgoingDeviceRequestSchema>;
export type TypedProspectiveCurrent = z.infer<typeof typedProspectiveCurrentSchema>;
export type BreakerSelectivityRequest = z.infer<typeof breakerSelectivityRequestSchema>;
export type BreakerSelectivityWarning = z.infer<typeof breakerSelectivityWarningSchema>;
export type BreakerDeviceResponse = z.infer<typeof breakerDeviceResponseSchema>;
export type BreakerSelectivityResponse = z.infer<typeof breakerSelectivityResponseSchema>;
export type BreakerSelectivityRunCreateRequest = z.infer<
  typeof breakerSelectivityRunCreateRequestSchema
>;
export type BreakerSelectivityRunResponse = z.infer<typeof breakerSelectivityRunResponseSchema>;

/**
 * Calculate a breaker duty and selectivity study and persist it as a run revision.
 *
 * The request carries the whole run body, because a study's notes belong to the
 * run, not to the checks the engine makes. With a project revision id the run is
 * stored under that revision of the selected project - and a linked fault run must
 * then belong to the same revision; without one it belongs to no project. An
 * unchanged study returns the stored revision with HTTP 200 instead of a new one
 * (A11); both are valid here.
 */
export async function createBreakerSelectivityRun(
  payload: BreakerSelectivityRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<BreakerSelectivityRunResponse> {
  const validatedPayload = breakerSelectivityRunCreateRequestSchema.parse(payload);

  const response = await fetch(RUNS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      formatApiError(data, response.status, "Breaker selectivity"),
      response.status,
    );
  }

  const parsed = breakerSelectivityRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error(
      "Unexpected response from the KES Electrical OS breaker selectivity runs API.",
    );
  }

  return parsed.data;
}
